import { logInfo, logError, logAPIError, logWarning } from '../utils/errorLogger'
import { log } from '../utils/logManager'

// Import API modules
import * as marketplaceApi from './api/marketplaceApi'
import * as farmProfileApi from './api/farmProfileApi'
import * as stlApi from './api/stlApi'
import * as adminApi from './api/adminApi'
import * as tokenApi from './api/tokenApi'
import * as insoleV2Api from './api/insoleV2Api'
import * as assistantApi from './api/assistantApi'
import type {
  AssistantChatResponse,
  AssistantChatTurn,
  AssistantHealth,
  AssistantTopic,
  ConversationSummary,
  ConversationMessage,
} from './api/assistantApi'

// Import internal response types (not re-exported)
import type { DoctorLoginResponse, FarmLoginResponse } from './types/auth'

// Import all types that the BackendService class needs
import type {
  BackendConfig,
  BackendMessage,
  HealthCheckResponse,
  LibraryData,
  AddDoctorResponse,
  Patient,
  Project,
  UserType,
  LoginResponse,
  TokenVerifyResponse,
  DoctorCreate,
  PatientCreate,
  ProjectCreate,
  ProjectStatusUpdate,
  TokenBalance,
  TokenHistoryResponse,
  AddTokensRequest,
  AddTokensResponse,
  DoctorAiTokenBalance,
  STLFileResponse,
  StlInStatusResponse,
  StlFileRole,
  InsoleGeneratePayload,
  InsoleGenerateStartResponse,
  MarketplaceJob,
  MarketplaceJobStatusUpdateResponse,
  MarketplaceWorkflowStatus,
  MarketplaceBid,
  MarketplaceQuote,
  PrintProfile,
  MarketplaceSliceEstimate,
  ManufacturingPackageResponse,
  MarketplaceJobOffersResponse,
  MarketplaceFarmSelectionResponse,
  MarketplaceFarm,
  MarketplaceJobCreateRequest,
  MarketplaceBidCreateRequest,
  MarketplaceQuoteCreateRequest,
  MarketplaceStlOption,
  MarketplaceNotification,
  FarmRegistrationCreate,
  FarmRegistrationResult,
  DoctorListResponse,
  FarmListResponse,
  PendingFarmVerificationsResponse,
  FarmProfile,
  FarmProfileUpdateRequest,
  FarmAvailability,
  FarmAvailabilityUpdateRequest,
  FarmPricing,
  FarmPricingUpdateRequest,
  FarmVerification,
  LandmarksResponse,
  ErrorLog,
  ErrorLogResponse,
  ErrorLogsResponse,
  SendMessageRequest,
  SendMessageResponse,
  TemplateListResponse,
  AlignRequest,
  AlignResponse,
  AlignAdjustRequest,
  AlignAdjustResponse,
  DrapeRequest,
  DrapeResponse,
  BoundaryAdjustRequest,
  BoundaryAdjustResponse,
  BoundarySmoothRequest,
  BoundarySmoothResponse,
  BoundaryApproveRequest,
  BoundaryApproveResponse,
  GenerateRequest,
  GenerateResponse,
  ExportFormat,
  ExportResponse,
  ApproveRequest,
  ApproveResponse,
  ValidateResponse,
  FarmAITokenTransaction
} from './types'

// Re-export all types for backward compatibility
export * from './types'

type ConnectionStatusListener = (connected: boolean, message?: string) => void
type BackendMessageListener = (message: BackendMessage) => void

class BackendService {
  private config: BackendConfig
  private authToken: string | null = null
  private doctorId: string | null = null
  private healthCheckTimer: NodeJS.Timeout | null = null
  private isConnected: boolean = false
  private connectionListeners: ConnectionStatusListener[] = []
  private messageListeners: BackendMessageListener[] = []

  constructor(config: BackendConfig) {
    this.config = config
    this.detectStoredSession()
    // Always start health checks to monitor connection status
    this.startHealthCheck()
  }

  /**
   * Detect stored user info on app start (no authToken — that's in httpOnly cookie, #99).
   * The cookie-based session is verified via /api/auth/verify on AuthContext mount.
   */
  private detectStoredSession(): void {
    this.doctorId = localStorage.getItem('userId') || localStorage.getItem('doctorId')
    // Dev/test support: seed in-memory auth token from localStorage
    // (production JWT is httpOnly cookie only, never stored in JS-accessible storage)
    const devToken = localStorage.getItem('__dev_auth_token')
    if (devToken && !this.authToken) {
      this.authToken = devToken
    }
    log.auth('debug', 'Detected stored user info', { doctorId: this.doctorId })
  }

  /**
   * Get authentication token (in-memory only, not persisted to localStorage, #99).
   */
  getAuthToken(): string | null {
    return this.authToken
  }

  /**
   * Set authentication credentials in memory only (#99).
   * The JWT is stored in an httpOnly cookie by the backend on login.
   */
  setAuth(userId: string, authToken: string): void {
    this.doctorId = userId
    this.authToken = authToken

    // Store non-sensitive user info for quick display on reload
    // (auth verification still happens via /verify on AuthContext mount).
    localStorage.setItem('userId', userId)
    localStorage.setItem('doctorId', userId)
    log.auth('info', 'Auth set in memory', { userId })
    this.startHealthCheck()
  }

  /**
   * Clear authentication and stop health checks
   */
  clearAuth(): void {
    this.authToken = null
    this.doctorId = null
    localStorage.removeItem('authToken')  // cleanup legacy storage
    localStorage.removeItem('userId')
    localStorage.removeItem('userName')
    localStorage.removeItem('userType')
    localStorage.removeItem('doctorId')
    localStorage.removeItem('doctorName')
    this.stopHealthCheck()
    logInfo('Authentication cleared', 'Backend')
  }

  /**
   * Add connection status listener
   */
  addConnectionListener(listener: ConnectionStatusListener): () => void {
    this.connectionListeners.push(listener)
    return () => {
      const index = this.connectionListeners.indexOf(listener)
      if (index > -1) {
        this.connectionListeners.splice(index, 1)
      }
    }
  }

  /**
   * Add backend message listener
   */
  addMessageListener(listener: BackendMessageListener): () => void {
    this.messageListeners.push(listener)
    return () => {
      const index = this.messageListeners.indexOf(listener)
      if (index > -1) {
        this.messageListeners.splice(index, 1)
      }
    }
  }

  /**
   * Notify connection status listeners
   */
  private notifyConnectionStatus(connected: boolean, message?: string): void {
    this.isConnected = connected
    this.connectionListeners.forEach(listener => {
      try {
        listener(connected, message)
      } catch (error) {
        console.warn('Error in connection listener:', error)
      }
    })
  }

  /**
   * Notify backend message listeners
   */
  private notifyBackendMessage(message: BackendMessage): void {
    this.messageListeners.forEach(listener => {
      try {
        listener(message)
      } catch (error) {
        console.warn('Error in message listener:', error)
      }
    })
  }

  /**
   * Send message from backend to frontend (local notification)
   */
  notifyMessage(type: 'info' | 'warning' | 'error', message: string, source: string = 'Backend'): void {
    const backendMessage: BackendMessage = {
      type,
      message,
      timestamp: new Date().toISOString(),
      source
    }
    this.notifyBackendMessage(backendMessage)
  }

  /**
   * Make authenticated API request
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs: number | null = this.config.timeout
  ): Promise<T> {
    const url = `${this.config.baseURL}${endpoint}`
    // Reduced logging: console.log(`BackendService: Making request to ${url}`)

    const headers = {
      'Content-Type': 'application/json',
      ...(this.authToken && { 'Authorization': `Bearer ${this.authToken}` }),
      ...options.headers
    }

    const controller = new AbortController()
    const timeoutId = timeoutMs && timeoutMs > 0
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
        signal: controller.signal
      })

      if (timeoutId) clearTimeout(timeoutId)
      // Reduced logging: console.log(`BackendService: Response status: ${response.status} ${response.statusText}`)

      if (!response.ok) {
        let errorData: any = {}
        let errorText = ''

        try {
          // Try to get response as text first
          errorText = await response.text()

          // Try to parse as JSON
          if (errorText) {
            try {
              errorData = JSON.parse(errorText)
            } catch (parseError) {
              // If JSON parsing fails, use text as message
              errorData = { message: errorText }
            }
          }
        } catch (readError) {
          console.warn('BackendService: Could not read error response:', readError)
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` }
        }

        // Enhanced error logging for HTTP 500
        if (response.status === 500) {
          console.error(`BackendService: HTTP 500 Server Error:`, {
            url,
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries()),
            errorData,
            errorText: errorText.substring(0, 500) // Limit text length
          })
        } else {
          console.error(`BackendService: Request failed with status ${response.status}:`, errorData)
        }

        // Create descriptive error message. FastAPI returns `detail` as either
        // a string or a structured object (e.g. {error, message, ...}); unwrap
        // object-shaped detail so users don't see "[object Object]".
        const detail = errorData.detail
        let errorMessage: string
        if (typeof detail === 'string') {
          errorMessage = detail
        } else if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
          errorMessage = detail.message
        } else if (typeof errorData.message === 'string') {
          errorMessage = errorData.message
        } else {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`
        }
        const error = new Error(errorMessage)

        // Add additional properties for better error handling
        ;(error as any).status = response.status
        ;(error as any).statusText = response.statusText
        ;(error as any).data = errorData
        ;(error as any).url = url

        throw error
      }

      const data = await response.json()
      // Reduced logging: console.log(`BackendService: Request successful, data:`, data)
      return data
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId)
      console.error(`BackendService: Request to ${url} failed:`, error)

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout')
        }
        throw error
      }
      throw new Error('Unknown error occurred')
    }
  }

  /**
   * Perform health check
   */
  async healthCheck(): Promise<HealthCheckResponse> {
    try {
      // Reduced logging: console.log('BackendService: Performing health check...')
      const response = await this.makeRequest<HealthCheckResponse>('/api/health')
      // Reduced logging: console.log('BackendService: Health check response:', response)

      // Consider the backend healthy if it responds with any status and has basic info
      // The backend may return "unhealthy" but still be functional for basic operations
      const isHealthy = response && (
        response.status === 'healthy' ||
        (response.status === 'unhealthy' && response.database === 'connected')
      )

      // Reduced logging: console.log(`BackendService: Health check result - isHealthy: ${isHealthy}, current connection: ${this.isConnected}`)

      if (isHealthy) {
        if (!this.isConnected) {
          logInfo('Backend connection established', 'Backend')
          this.notifyConnectionStatus(true)
        }
      } else {
        logWarning(`Backend health check returned: ${response.status}`, 'Backend')
        this.notifyConnectionStatus(false, response.message || 'Backend reported unhealthy status')
      }

      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Health check failed'
      console.error('BackendService: Health check failed:', error)
      logError(`Backend health check failed: ${errorMessage}`, 'Backend')
      this.notifyConnectionStatus(false, errorMessage)
      throw error
    }
  }

  private async makeBinaryRequest(endpoint: string, options: RequestInit = {}): Promise<ArrayBuffer> {
    const url = `${this.config.baseURL}${endpoint}`
    const headers = {
      ...(this.authToken && { 'Authorization': `Bearer ${this.authToken}` }),
      ...options.headers
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout)

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      return await response.arrayBuffer()
    } finally {
      clearTimeout(timeoutId)
    }
  }

  /**
   * Start periodic health checks
   */
  private startHealthCheck(): void {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer)
    }

    // Initial health check
    this.healthCheck().catch(() => {
      // Error already logged in healthCheck method
    })

    // Periodic health checks
    this.healthCheckTimer = setInterval(() => {
      this.healthCheck().catch(() => {
        // Error already logged in healthCheck method
      })
    }, this.config.healthCheckInterval)

    logInfo(`Health check started (interval: ${this.config.healthCheckInterval}ms)`, 'Backend')
  }

  /**
   * Stop health checks
   */
  private stopHealthCheck(): void {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer)
      this.healthCheckTimer = null
      logInfo('Health check stopped', 'Backend')
    }
  }

  /**
   * Get library data for authenticated doctor
   */
  async getLibraryData(): Promise<LibraryData> {
    if (!this.doctorId) {
      throw new Error('No doctor ID available')
    }

    try {
      log.data('info', 'Fetching library', { doctorId: this.doctorId })
      const url = `/api/doctors/${this.doctorId}/library`
      log.net('debug', `GET ${url}`)
      const data = await this.makeRequest<LibraryData>(url)
      const patients = data.doctors?.[0]?.patients || []
      log.data('info', 'Library loaded', { doctors: data.doctors.length, patients: patients.length })
      log.data('debug', 'Library response', {
        doctorId: data.doctors?.[0]?.id,
        doctorName: data.doctors?.[0]?.name,
        patientNames: patients.map(p => p.name).join(', '),
        patientIds: patients.map(p => p.id).join(', '),
      })
      return data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load library data'
      log.data('error', 'Library fetch failed', { doctorId: this.doctorId, error: errorMessage })
      throw error
    }
  }

  /**
   * Login doctor or farm
   */
  async login(username: string, password: string, userType: UserType = 'doctor'): Promise<LoginResponse> {
    try {
      log.auth('info', 'Login attempt', { username, userType })
      const endpoint = userType === 'farm' ? '/api/farms/login' : '/api/auth/login'
      const rawResponse = await this.makeRequest<DoctorLoginResponse | FarmLoginResponse>(endpoint, {
        method: 'POST',
        body: JSON.stringify({ username, password })
      })

      const response: LoginResponse = userType === 'farm'
        ? {
            success: rawResponse.success,
            userType: 'farm',
            userId: (rawResponse as FarmLoginResponse).farmId,
            userName: (rawResponse as FarmLoginResponse).businessName,
            authToken: rawResponse.authToken,
            farmId: (rawResponse as FarmLoginResponse).farmId,
            businessName: (rawResponse as FarmLoginResponse).businessName,
            isAdmin: false
          }
        : {
            success: rawResponse.success,
            userType: 'doctor',
            userId: (rawResponse as DoctorLoginResponse).doctorId,
            userName: (rawResponse as DoctorLoginResponse).doctorName,
            authToken: rawResponse.authToken,
            isAdmin: (rawResponse as DoctorLoginResponse).isAdmin || false,
            doctorId: (rawResponse as DoctorLoginResponse).doctorId,
            doctorName: (rawResponse as DoctorLoginResponse).doctorName
          }

      if (response.success) {
        // setAuth stores userId/doctorId in memory and localStorage (#99)
        this.setAuth(response.userId, response.authToken)
        localStorage.setItem('userName', response.userName)
        localStorage.setItem('userType', response.userType)

        if (response.userType === 'doctor' && response.doctorName) {
          localStorage.setItem('doctorName', response.doctorName)
        } else {
          localStorage.removeItem('doctorName')
        }

        log.auth('info', 'Login OK', { userId: response.userId, userName: response.userName, userType: response.userType })
      }

      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      log.auth('error', 'Login failed', { username, userType, error: errorMessage })
      throw error
    }
  }

  /**
   * Smart login: tries doctor first, then farm. Removes the need for a manual toggle.
   */
  async smartLogin(username: string, password: string): Promise<LoginResponse> {
    try {
      return await this.login(username, password, 'doctor')
    } catch (_doctorErr) {
      try {
        return await this.login(username, password, 'farm')
      } catch (_farmErr) {
        throw new Error('Invalid username or password')
      }
    }
  }

  /**
   * Verify authentication token
   */
  async verifyToken(): Promise<TokenVerifyResponse> {
    try {
      const response = await this.makeRequest<Omit<TokenVerifyResponse, 'userType'>>('/api/auth/verify', {
        method: 'POST'
      })
      const normalizedResponse: TokenVerifyResponse = {
        ...response,
        userType: 'doctor'
      }
      logInfo(`Token verification successful for: ${normalizedResponse.doctorName}`, 'Backend')
      // AuthContext handles localStorage updates for user info (#99)
      return normalizedResponse
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Token verification failed'
      logAPIError('/api/auth/verify', 'POST', 0, errorMessage)
      // Clear invalid auth on verification failure
      this.clearAuth()
      throw error
    }
  }

  /**
   * Logout and invalidate token
   */
  async logout(): Promise<void> {
    try {
      await this.makeRequest('/api/auth/logout', {
        method: 'POST'
      })
      this.clearAuth()
      logInfo('Logout successful', 'Backend')
    } catch (error) {
      // Clear auth even if logout request fails
      this.clearAuth()
      const errorMessage = error instanceof Error ? error.message : 'Logout failed'
      logAPIError('/api/auth/logout', 'POST', 0, errorMessage)
      throw error
    }
  }

  /**
   * Add new doctor
   */
  async addDoctor(doctorData: DoctorCreate): Promise<AddDoctorResponse> {
    try {
      // Validate required fields
      if (!doctorData.name || !doctorData.name.trim()) {
        throw new Error('Doctor name is required')
      }

      // Clean the doctor data
      const cleanedDoctorData: DoctorCreate = {
        name: doctorData.name.trim(),
        specialization: doctorData.specialization?.trim() || undefined,
        contactInfo: doctorData.contactInfo ? {
          ...(doctorData.contactInfo.email && { email: doctorData.contactInfo.email.trim() }),
          ...(doctorData.contactInfo.phone && { phone: doctorData.contactInfo.phone.trim() })
        } : undefined,
        credentials: doctorData.credentials ? {
          username: doctorData.credentials.username.trim(),
          password: doctorData.credentials.password
        } : undefined
      }

      logInfo('Adding new doctor', 'Backend')

      const response = await this.makeRequest<AddDoctorResponse>('/api/doctors', {
        method: 'POST',
        body: JSON.stringify(cleanedDoctorData)
      })

      logInfo(`Doctor added successfully (ID: ${response.doctorId})`, 'Backend')
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add doctor'
      logAPIError('/api/doctors', 'POST', 0, errorMessage)
      throw error
    }
  }

  /**
   * Register a new farm
   */
  async registerFarm(farmData: FarmRegistrationCreate): Promise<FarmRegistrationResult> {
    try {
      if (!farmData.businessName?.trim()) {
        throw new Error('Business name is required')
      }

      if (!farmData.ownerName?.trim()) {
        throw new Error('Owner name is required')
      }

      if (!farmData.credentials?.username?.trim()) {
        throw new Error('Username is required')
      }

      if (!farmData.credentials?.password) {
        throw new Error('Password is required')
      }

      if (!farmData.email?.trim()) {
        throw new Error('Email is required')
      }

      if (!farmData.address?.line1?.trim()) {
        throw new Error('Address line 1 is required')
      }

      if (!farmData.address?.city?.trim()) {
        throw new Error('City is required')
      }

      if (!farmData.address?.country?.trim()) {
        throw new Error('Country is required')
      }

      const cleanedFarmData = {
        business_name: farmData.businessName.trim(),
        owner_name: farmData.ownerName.trim(),
        username: farmData.credentials.username.trim(),
        password: farmData.credentials.password,
        email: farmData.email.trim().toLowerCase(),
        phone: farmData.phone?.trim() || undefined,
        website: farmData.website?.trim() || undefined,
        description: farmData.description?.trim() || undefined,
        address: {
          line1: farmData.address.line1.trim(),
          line2: farmData.address.line2?.trim() || undefined,
          city: farmData.address.city.trim(),
          state_province: farmData.address.stateProvince?.trim() || undefined,
          postal_code: farmData.address.postalCode?.trim() || undefined,
          country: farmData.address.country.trim().toUpperCase()
        },
        capabilities: {
          printer_count: Math.max(1, farmData.capabilities?.printerCount || 1),
          materials_available: farmData.capabilities?.materialsAvailable?.filter(Boolean)?.length
            ? farmData.capabilities.materialsAvailable.map(material => material.trim()).filter(Boolean)
            : ['PLA'],
          medical_grade_capable: farmData.capabilities?.medicalGradeCapable || false
        }
      }

      logInfo('Registering new farm', 'Backend')

      const response = await this.makeRequest<FarmRegistrationResult>('/api/farms/register', {
        method: 'POST',
        body: JSON.stringify(cleanedFarmData)
      })

      logInfo(`Farm registered successfully (ID: ${response.farmId})`, 'Backend')
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to register farm'
      logAPIError('/api/farms/register', 'POST', 0, errorMessage)
      throw error
    }
  }

  /**
   * Add new patient
   */
  async addPatient(doctorId: string, patientData: PatientCreate): Promise<Patient> {
    try {
      // Validate required fields
      if (!patientData.name || !patientData.name.trim()) {
        throw new Error('Patient name is required')
      }

      if (!doctorId || !doctorId.trim()) {
        throw new Error('Doctor ID is required')
      }

      // Clean and validate the patient data
      const cleanedPatientData: PatientCreate = {
        name: patientData.name.trim(),
        dateOfBirth: patientData.dateOfBirth || undefined,
        contactInfo: patientData.contactInfo ? {
          ...(patientData.contactInfo.email && { email: patientData.contactInfo.email.trim() }),
          ...(patientData.contactInfo.phone && { phone: patientData.contactInfo.phone.trim() })
        } : undefined,
        medicalInfo: patientData.medicalInfo ? {
          ...(patientData.medicalInfo.conditions && { conditions: patientData.medicalInfo.conditions }),
          ...(patientData.medicalInfo.notes && { notes: patientData.medicalInfo.notes.trim() })
        } : undefined
      }

      logInfo(`Adding new patient to doctor: ${doctorId}`, 'Backend')

      const patient = await this.makeRequest<Patient>(`/api/doctors/${doctorId}/patients`, {
        method: 'POST',
        body: JSON.stringify(cleanedPatientData)
      })

      logInfo(`Patient added successfully (ID: ${patient.id})`, 'Backend')
      return patient
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add patient'
      logAPIError(`/api/doctors/${doctorId}/patients`, 'POST', 0, errorMessage)
      this.notifyMessage('error', `Add patient failed: ${errorMessage}`, 'Frontend')
      throw error
    }
  }

  /**
   * Create new project
   */
  async createProject(projectData: ProjectCreate): Promise<Project> {
    try {
      logInfo(`Creating new project: ${projectData.name}`, 'Backend')
      const project = await this.makeRequest<Project>('/api/projects', {
        method: 'POST',
        body: JSON.stringify(projectData)
      })
      logInfo(`Project created successfully: ${project.name} (ID: ${project.id})`, 'Backend')
      return project
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create project'
      logAPIError('/api/projects', 'POST', 0, errorMessage, projectData)
      throw error
    }
  }

  /**
   * Update project status
   */
  async updateProjectStatus(projectId: string, statusData: ProjectStatusUpdate): Promise<{ success: boolean; projectId: string; newStatus: string; updatedDate: string }> {
    try {
      logInfo(`Updating project status: ${projectId} to ${statusData.status}`, 'Backend')
      const response = await this.makeRequest<{ success: boolean; projectId: string; newStatus: string; updatedDate: string }>(`/api/projects/${projectId}/status`, {
        method: 'PUT',
        body: JSON.stringify(statusData)
      })
      logInfo(`Project status updated successfully: ${projectId}`, 'Backend')
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update project status'
      logAPIError(`/api/projects/${projectId}/status`, 'PUT', 0, errorMessage, statusData)
      throw error
    }
  }

  /**
   * Delete project (if backend supports it)
   */
  async deleteProject(projectId: string): Promise<any> {
    try {
      logInfo(`Deleting project: ${projectId}`,'Backend')
      const response = await this.makeRequest<any>(`/api/projects/${projectId}`, { method: 'DELETE' })
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete project'
      logAPIError(`/api/projects/${projectId}`, 'DELETE', 0, errorMessage)
      throw error
    }
  }

  /**
   * Delete patient (if backend supports it)
   */
  async deletePatient(patientId: string): Promise<any> {
    try {
      logInfo(`Deleting patient: ${patientId}`,'Backend')
      const response = await this.makeRequest<any>(`/api/patients/${patientId}`, { method: 'DELETE' })
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete patient'
      logAPIError(`/api/patients/${patientId}`, 'DELETE', 0, errorMessage)
      throw error
    }
  }

  /**
   * Delete STL file(s) for a project
   */
  /**
   * Download STL file as ArrayBuffer (with auth). Used by viewers that need
   * to load STL geometry into Three.js — STLLoader's plain fetch cannot send
   * the Authorization header, so we fetch the bytes ourselves and create a
   * Blob URL from them.
   */
  async downloadStlAsArrayBuffer(projectId: string, fileRole: StlFileRole = 'stl_in'): Promise<ArrayBuffer> {
    const routeSegment = fileRole === 'stl_out' ? 'stl-out' : 'stl-in'
    return this.makeBinaryRequest(`/api/projects/${projectId}/${routeSegment}/download`)
  }

  async deleteSTLFile(projectId: string, fileRole: StlFileRole = 'stl_in'): Promise<any> {
    return stlApi.deleteSTLFile(this.makeRequest.bind(this), projectId, fileRole)
  }


  /**
   * Upload STL file
   */
  async uploadSTLFile(
    file: File,
    projectId?: string,
    patientId?: string,
    doctorId?: string,
    originalBoundingBox?: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number }; size: { x: number; y: number; z: number } },
    fileType: StlFileRole = 'stl_in'
  ): Promise<STLFileResponse> {
    return stlApi.uploadSTLFile(this.config.baseURL, this.authToken, this.config.timeout, file, projectId, patientId, doctorId, originalBoundingBox, fileType)
  }

  /**
   * Upload STL file with XHR for real upload progress tracking
   */
  async uploadSTLFileWithProgress(
    file: File,
    projectId?: string,
    patientId?: string,
    doctorId?: string,
    originalBoundingBox?: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number }; size: { x: number; y: number; z: number } },
    fileType: StlFileRole = 'stl_in',
    onProgress?: (progress: { percentage: number; bytesUploaded: number; totalBytes: number }) => void
  ): Promise<STLFileResponse> {
    return stlApi.uploadSTLFileWithProgress(this.config.baseURL, this.authToken, file, projectId, patientId, doctorId, originalBoundingBox, fileType, onProgress)
  }

  async getStlStatus(projectId: string, fileRole: StlFileRole = 'stl_in'): Promise<StlInStatusResponse> {
    return stlApi.getStlStatus(this.makeRequest.bind(this), this.authToken, projectId, fileRole)
  }

  /**
   * Get STL_IN existence status for a project
   */
  async getStlInStatus(projectId: string): Promise<StlInStatusResponse> {
    return stlApi.getStlStatus(this.makeRequest.bind(this), this.authToken, projectId, 'stl_in')
  }

  async getStlOutStatus(projectId: string): Promise<StlInStatusResponse> {
    return stlApi.getStlStatus(this.makeRequest.bind(this), this.authToken, projectId, 'stl_out')
  }

  /**
   * Start background generation of an insole (async + polling via stl-out/status).
   */
  async generateInsole(
    projectId: string,
    payload: InsoleGeneratePayload
  ): Promise<InsoleGenerateStartResponse> {
    try {
      logInfo(`Generating insole for project: ${projectId}`, 'Backend')
      const response = await this.makeRequest<InsoleGenerateStartResponse>(`/api/projects/${projectId}/insole/generate`, {
        method: 'POST',
        body: JSON.stringify(payload)
      }, null)
      logInfo(`✅ Insole generation started (requestId=${response.requestId})`, 'Backend')
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start insole generation'
      logAPIError(`/api/projects/${projectId}/insole/generate`, 'POST', 0, errorMessage, payload)
      throw error
    }
  }

  /**
   * Get STL file metadata
   */
  async getSTLMetadata(fileId: string): Promise<STLFileResponse> {
    return stlApi.getSTLMetadata(this.makeRequest.bind(this), fileId)
  }

  /**
   * Save calibration points for an STL file
   */
  async saveCalibration(
    fileId: string,
    calibrationPoints: {
      heel: { x: number; y: number; z: number }
      mt1: { x: number; y: number; z: number }
      mt5: { x: number; y: number; z: number }
      invert_front_back?: boolean
      invert_top_bottom?: boolean
    }
  ): Promise<{ success: boolean; message: string }> {
    return stlApi.saveCalibration(this.makeRequest.bind(this), fileId, calibrationPoints)
  }

  /**
   * Get calibration data for an STL file
   */
  async getCalibration(fileId: string): Promise<{
    calibrated: boolean
    calibrationPoints?: {
      heel: { x: number; y: number; z: number }
      mt1: { x: number; y: number; z: number }
      mt5: { x: number; y: number; z: number }
    }
    orientationMatrix?: number[][]
  }> {
    return stlApi.getCalibration(this.makeRequest.bind(this), fileId)
  }

  /**
   * Get foot measurements for a calibrated STL file
   */
  async getMeasurements(fileId: string): Promise<{
    calibrated: boolean
    measurements?: {
      foot_length: number
      foot_width: number
      arch_height: number
      arch_classification: string
      heel_width: number
      instep_height: number
      units: string
    }
    message?: string
  }> {
    return stlApi.getMeasurements(this.makeRequest.bind(this), fileId)
  }

  /**
   * Get landmarks for an STL file
   */
  async getLandmarks(
    fileId: string,
    filters?: {
      landmark_type?: string
      detection_method?: string
      min_confidence?: number
    }
  ): Promise<LandmarksResponse> {
    return stlApi.getLandmarks(this.makeRequest.bind(this), fileId, filters)
  }

  /**
   * Get STL download URL for a project
   */
  getSTLDownloadUrl(projectId: string, fileRole: StlFileRole = 'stl_in'): string {
    return stlApi.getSTLDownloadUrl(this.config.baseURL, projectId, fileRole)
  }

  /**
   * Get the base URL for the backend API
   */
  getBaseURL(): string {
    return this.config.baseURL
  }

  /**
   * Log frontend error
   */
  async logError(errorData: ErrorLog): Promise<ErrorLogResponse> {
    try {
      const response = await this.makeRequest<ErrorLogResponse>('/api/errors/log', {
        method: 'POST',
        body: JSON.stringify(errorData)
      })
      return response
    } catch (error) {
      // Don't log errors about logging errors to avoid infinite loops
      console.warn('Failed to log error to backend:', error)
      throw error
    }
  }

  /**
   * Get error logs
   */
  async getErrorLogs(limit: number = 100, offset: number = 0, type?: string, doctorId?: string): Promise<ErrorLogsResponse> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      })
      if (type) params.append('type', type)
      if (doctorId) params.append('doctorId', doctorId)

      const response = await this.makeRequest<ErrorLogsResponse>(`/api/errors?${params}`)
      logInfo(`Retrieved ${response.errors.length} error logs`, 'Backend')
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get error logs'
      logAPIError('/api/errors', 'GET', 0, errorMessage)
      throw error
    }
  }

  /**
   * Send message to backend/frontend communication system
   */
  async sendMessage(messageData: SendMessageRequest): Promise<SendMessageResponse> {
    try {
      logInfo(`Sending message: ${messageData.type} - ${messageData.message}`, 'Backend')
      const response = await this.makeRequest<SendMessageResponse>('/api/messages/send', {
        method: 'POST',
        body: JSON.stringify(messageData)
      })
      logInfo(`Message sent successfully, delivered to ${response.deliveredTo} connections`, 'Backend')
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message'
      logAPIError('/api/messages/send', 'POST', 0, errorMessage, messageData)
      throw error
    }
  }

  // ==================== ADMIN ENDPOINTS ====================

  /**
   * List all doctors (admin only)
   */
  async listAllDoctors(
    page: number = 1,
    limit: number = 20,
    search?: string,
    sortBy: string = 'name',
    sortOrder: 'asc' | 'desc' = 'asc',
    activeOnly: boolean = false
  ): Promise<DoctorListResponse> {
    return adminApi.listAllDoctors(this.makeRequest.bind(this), page, limit, search, sortBy, sortOrder, activeOnly)
  }

  /**
   * List all farms (admin only)
   */
  async listAllFarms(
    page: number = 1,
    limit: number = 20,
    search?: string,
    sortBy: string = 'business_name',
    sortOrder: 'asc' | 'desc' = 'asc',
    activeOnly: boolean = false
  ): Promise<FarmListResponse> {
    return adminApi.listAllFarms(this.makeRequest.bind(this), page, limit, search, sortBy, sortOrder, activeOnly)
  }

  /**
   * List farms pending verification review (admin only)
   */
  async listPendingFarmVerifications(): Promise<PendingFarmVerificationsResponse> {
    return adminApi.listPendingFarmVerifications(this.makeRequest.bind(this))
  }

  /**
   * List V2 insole QA runs (admin only).
   */
  async listV2QARuns(
    opts: { limit?: number; offset?: number; patient_id?: string } = {}
  ): Promise<adminApi.V2QARunListResponse> {
    return adminApi.listV2QARuns(this.makeRequest.bind(this), opts)
  }

  /**
   * Get a single V2 insole QA run by id (admin only).
   */
  async getV2QARun(runId: string): Promise<adminApi.V2QARunDetail> {
    return adminApi.getV2QARun(this.makeRequest.bind(this), runId)
  }

  /**
   * Update a farm's verification status (admin only)
   */
  async updateFarmVerificationStatus(
    farmId: string,
    status: string,
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    return adminApi.updateFarmVerificationStatus(this.makeRequest.bind(this), farmId, status, notes)
  }

  /**
   * Delete a doctor account permanently (admin only, requires admin password)
   */
  async deleteDoctorAccount(doctorId: string, adminPassword: string): Promise<{ ok: boolean; doctorId: string; name: string; username: string; message: string }> {
    return adminApi.deleteDoctorAccount(this.makeRequest.bind(this), doctorId, adminPassword)
  }

  /**
   * Delete a farm account permanently (admin only, requires admin password)
   */
  async deleteFarmAccount(farmId: string, adminPassword: string): Promise<{ ok: boolean; farmId: string; businessName: string; message: string }> {
    return adminApi.deleteFarmAccount(this.makeRequest.bind(this), farmId, adminPassword)
  }

  /**
   * Get token balance for a specific doctor (admin only)
   */
  async getDoctorTokenBalance(doctorId: string): Promise<TokenBalance> {
    return tokenApi.getDoctorTokenBalance(this.makeRequest.bind(this), doctorId)
  }

  /**
   * Add tokens to a doctor's account (admin only)
   */
  async addDoctorTokens(doctorId: string, request: AddTokensRequest): Promise<AddTokensResponse> {
    return tokenApi.addDoctorTokens(this.makeRequest.bind(this), doctorId, request)
  }

  /**
   * Get token transaction history for a doctor (admin only)
   */
  async getDoctorTokenHistory(
    doctorId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<TokenHistoryResponse> {
    return tokenApi.getDoctorTokenHistory(this.makeRequest.bind(this), doctorId, page, limit)
  }

  /**
   * Get token balance for the current authenticated doctor
   */
  async getTokenBalance(): Promise<TokenBalance> {
    return tokenApi.getTokenBalance(this.makeRequest.bind(this))
  }

  // ==================== DOCTOR AI TOKEN ENDPOINTS ====================

  /**
   * Get AI token balance for a specific doctor (admin only)
   */
  async getDoctorAiTokenBalance(doctorId: string): Promise<DoctorAiTokenBalance> {
    return tokenApi.getDoctorAiTokenBalance(this.makeRequest.bind(this), doctorId)
  }

  /**
   * Get AI token balance for the current authenticated doctor (self-service)
   */
  async getMyAiTokenBalance(): Promise<DoctorAiTokenBalance> {
    return tokenApi.getMyAiTokenBalance(this.makeRequest.bind(this))
  }

  /**
   * Credit (add) AI tokens to a doctor's account (admin only)
   */
  async creditDoctorAiTokens(doctorId: string, amount: number, reason: string): Promise<DoctorAiTokenBalance> {
    return tokenApi.creditDoctorAiTokens(this.makeRequest.bind(this), doctorId, amount, reason)
  }

  /**
   * Debit (deduct) AI tokens from a doctor's account (admin only)
   */
  async debitDoctorAiTokens(doctorId: string, amount: number, reason: string): Promise<DoctorAiTokenBalance> {
    return tokenApi.debitDoctorAiTokens(this.makeRequest.bind(this), doctorId, amount, reason)
  }

  // ==================== MARKETPLACE ENDPOINTS ====================

  /**
   * List marketplace jobs for the current doctor
   */
  async listMarketplaceJobs(): Promise<MarketplaceJob[]> {
    return marketplaceApi.listMarketplaceJobs(this.makeRequest.bind(this))
  }

  async listPrintProfiles(): Promise<PrintProfile[]> {
    return marketplaceApi.listPrintProfiles(this.makeRequest.bind(this))
  }

  async createPrintProfile(profile: Omit<PrintProfile, 'id'>): Promise<PrintProfile> {
    return marketplaceApi.createPrintProfile(this.makeRequest.bind(this), profile)
  }

  async importPrintProfile(file: File): Promise<PrintProfile> {
    return marketplaceApi.importPrintProfile(this.config.baseURL, this.authToken, file)
  }

  async exportPrintProfile(profileId: string): Promise<Record<string, any>> {
    return marketplaceApi.exportPrintProfile(this.makeRequest.bind(this), profileId)
  }

  async estimateMarketplaceJob(input: {
    stlFileId: string
    profileId?: string
    profileOverrides?: Record<string, any>
  }): Promise<{ profile: Record<string, any>; stlMetrics: Record<string, any>; estimate: MarketplaceSliceEstimate }> {
    return marketplaceApi.estimateMarketplaceJob(this.makeRequest.bind(this), input)
  }

  async getMarketplaceJobManufacturingPackage(jobId: string): Promise<ManufacturingPackageResponse> {
    return marketplaceApi.getMarketplaceJobManufacturingPackage(this.makeRequest.bind(this), jobId)
  }

  async estimateMarketplaceJobForFarm(jobId: string, input: {
    profileId?: string
    profileOverrides?: Record<string, any>
  }): Promise<{ profile: Record<string, any>; stlMetrics: Record<string, any>; estimate: MarketplaceSliceEstimate }> {
    return marketplaceApi.estimateMarketplaceJobForFarm(this.makeRequest.bind(this), jobId, input)
  }

  async downloadMarketplaceJobSTL(jobId: string): Promise<ArrayBuffer> {
    return marketplaceApi.downloadMarketplaceJobSTL(this.makeBinaryRequest.bind(this), jobId)
  }

  /**
   * Create a marketplace job for the current doctor
   */
  async createMarketplaceJob(job: MarketplaceJobCreateRequest): Promise<MarketplaceJob> {
    return marketplaceApi.createMarketplaceJob(this.makeRequest.bind(this), job)
  }

  /**
   * Load doctor-owned job offers
   */
  async getMarketplaceJobOffers(jobId: string): Promise<MarketplaceJobOffersResponse> {
    return marketplaceApi.getMarketplaceJobOffers(this.makeRequest.bind(this), jobId)
  }

  /**
   * Load doctor-owned job bids
   */
  async getMarketplaceJobBids(jobId: string): Promise<MarketplaceBid[]> {
    return marketplaceApi.getMarketplaceJobBids(this.makeRequest.bind(this), jobId)
  }

  /**
   * Accept a marketplace bid
   */
  async acceptMarketplaceBid(bidId: string): Promise<{ success: boolean; bidId: string; jobId: string; message: string }> {
    return marketplaceApi.acceptMarketplaceBid(this.makeRequest.bind(this), bidId)
  }

  /**
   * Select a bid or fixed-price quote for a marketplace job
   */
  async selectMarketplaceFarm(jobId: string, selection: { bidId?: string; quoteId?: string; paymentMethod?: 'card' }): Promise<MarketplaceFarmSelectionResponse> {
    return marketplaceApi.selectMarketplaceFarm(this.makeRequest.bind(this), jobId, selection)
  }

  /**
   * List farms available in the marketplace
   */
  async listMarketplaceFarms(): Promise<MarketplaceFarm[]> {
    return marketplaceApi.listMarketplaceFarms(this.makeRequest.bind(this))
  }

  /**
   * List jobs available to farms
   */
  async listAvailableMarketplaceJobs(): Promise<MarketplaceJob[]> {
    return marketplaceApi.listAvailableMarketplaceJobs(this.makeRequest.bind(this))
  }

  /**
   * List active marketplace jobs assigned to the current farm
   */
  async listAssignedMarketplaceJobs(): Promise<MarketplaceJob[]> {
    return marketplaceApi.listAssignedMarketplaceJobs(this.makeRequest.bind(this))
  }

  /**
   * Submit a marketplace bid
   */
  async submitMarketplaceBid(
    jobId: string,
    bid: MarketplaceBidCreateRequest
  ): Promise<{ success: boolean; bidId: string; message: string }> {
    return marketplaceApi.submitMarketplaceBid(this.makeRequest.bind(this), jobId, bid)
  }

  /**
   * Submit a marketplace quote for a fixed-price job
   */
  async submitMarketplaceQuote(
    jobId: string,
    quote: MarketplaceQuoteCreateRequest
  ): Promise<{ success: boolean; quoteId: string; message: string }> {
    return marketplaceApi.submitMarketplaceQuote(this.makeRequest.bind(this), jobId, quote)
  }

  /**
   * List the current farm's submitted marketplace bids
   */
  async listMyMarketplaceBids(): Promise<MarketplaceBid[]> {
    return marketplaceApi.listMyMarketplaceBids(this.makeRequest.bind(this))
  }

  /**
   * List the current farm's submitted marketplace quotes
   */
  async listMyMarketplaceQuotes(): Promise<MarketplaceQuote[]> {
    return marketplaceApi.listMyMarketplaceQuotes(this.makeRequest.bind(this))
  }

  /**
   * Cancel a pending marketplace bid owned by the current farm
   */
  async withdrawMarketplaceBid(bidId: string): Promise<{ success: boolean; bid: MarketplaceBid; message: string }> {
    return marketplaceApi.withdrawMarketplaceBid(this.makeRequest.bind(this), bidId)
  }

  /**
   * Cancel a pending marketplace quote owned by the current farm
   */
  async withdrawMarketplaceQuote(quoteId: string): Promise<{ success: boolean; quote: MarketplaceQuote; message: string }> {
    return marketplaceApi.withdrawMarketplaceQuote(this.makeRequest.bind(this), quoteId)
  }

  /**
   * Advance the workflow status of an active marketplace job assigned to the current farm
   */
  async updateMarketplaceJobStatus(
    jobId: string,
    status: MarketplaceWorkflowStatus
  ): Promise<MarketplaceJobStatusUpdateResponse> {
    return marketplaceApi.updateMarketplaceJobStatus(this.makeRequest.bind(this), jobId, status)
  }

  /**
   * Confirm a shipped marketplace job as delivered (doctor side)
   */
  async confirmMarketplaceJobDelivered(
    jobId: string
  ): Promise<{ success: boolean; jobId: string; status: MarketplaceWorkflowStatus; job: MarketplaceJob; message: string }> {
    return marketplaceApi.confirmMarketplaceJobDelivered(this.makeRequest.bind(this), jobId)
  }

  /**
   * List marketplace inbox notifications for the current doctor or farm
   */
  async listMarketplaceNotifications(): Promise<{ notifications: MarketplaceNotification[]; unreadCount: number }> {
    return marketplaceApi.listMarketplaceNotifications(this.makeRequest.bind(this))
  }

  /**
   * Mark a marketplace inbox notification as read
   */
  async markMarketplaceNotificationRead(notificationId: string): Promise<MarketplaceNotification> {
    return marketplaceApi.markMarketplaceNotificationRead(this.makeRequest.bind(this), notificationId)
  }

  /**
   * Get the authenticated farm's profile
   */
  async getFarmProfile(): Promise<FarmProfile> {
    return farmProfileApi.getFarmProfile(this.makeRequest.bind(this))
  }

  /**
   * Update the authenticated farm's profile
   */
  async updateFarmProfile(profile: FarmProfileUpdateRequest): Promise<FarmProfile> {
    return farmProfileApi.updateFarmProfile(this.makeRequest.bind(this), profile)
  }

  /**
   * Update the authenticated farm's availability
   */
  async updateFarmAvailability(availability: FarmAvailabilityUpdateRequest): Promise<FarmAvailability> {
    return farmProfileApi.updateFarmAvailability(this.makeRequest.bind(this), availability)
  }

  /**
   * Get the authenticated farm's pricing configuration
   */
  async getFarmPricing(): Promise<FarmPricing> {
    return farmProfileApi.getFarmPricing(this.makeRequest.bind(this))
  }

  /**
   * Update the authenticated farm's pricing configuration
   */
  async updateFarmPricing(pricing: FarmPricingUpdateRequest): Promise<FarmPricing> {
    return farmProfileApi.updateFarmPricing(this.makeRequest.bind(this), pricing)
  }

  /**
   * Upload a verification document for the authenticated farm
   */
  async uploadFarmVerificationDocument(
    file: File,
    documentType: string,
    notes?: string
  ): Promise<FarmVerification> {
    return farmProfileApi.uploadFarmVerificationDocument(this.config.baseURL, this.authToken, file, documentType, notes)
  }

  /**
   * List STL-backed options that can be published as marketplace jobs
   */
  async listMarketplaceStlOptions(): Promise<MarketplaceStlOption[]> {
    try {
      const library = await this.getLibraryData()
      const projectContexts = library.doctors.flatMap(doctor =>
        doctor.patients.flatMap(patient =>
          patient.projects.map(project => ({ patient, project }))
        )
      )

      const options = await Promise.all(projectContexts.map(async ({ patient, project }) => {
        try {
          const status = await this.getStlInStatus(project.id)

          if (!status.exists || !status.latest?.fileId) {
            return null
          }

          const stlFileName = status.latest.filename || status.latest.fileId

          return {
            projectId: project.id,
            projectName: project.name,
            patientId: patient.id,
            patientName: patient.name,
            stlFileId: status.latest.fileId,
            stlFileName,
            label: `${patient.name} / ${project.name} / ${stlFileName}`
          } satisfies MarketplaceStlOption
        } catch (projectError) {
          const errorMessage = projectError instanceof Error ? projectError.message : 'Unknown STL status error'
          logWarning(`Skipping marketplace STL option for project ${project.id}: ${errorMessage}`, 'Backend')
          return null
        }
      }))

      return options
        .filter((option): option is MarketplaceStlOption => Boolean(option))
        .sort((a, b) => a.label.localeCompare(b.label))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load marketplace STL options'
      logAPIError('/api/marketplace/stl-options', 'GET', 0, errorMessage)
      throw error
    }
  }

  // ==================== WEBSOCKET ====================

  /**
   * Connect to WebSocket for real-time communication
   */
  connectWebSocket(): WebSocket | null {
    if (!this.doctorId) {
      logWarning('Cannot connect WebSocket: No doctor ID available', 'Backend')
      return null
    }

    try {
      const wsUrl = this.config.baseURL.replace('http', 'ws') + `/ws/doctor/${this.doctorId}`
      logInfo(`Connecting to WebSocket: ${wsUrl}`, 'Backend')
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        logInfo('WebSocket connection established', 'Backend')
        // Send initial ping to keep connection alive
        try {
          ws.send(JSON.stringify({
            type: 'ping',
            timestamp: new Date().toISOString()
          }))
          logInfo('Initial ping sent to WebSocket', 'Backend')
        } catch (error) {
          logError('Failed to send initial ping to WebSocket', 'Backend')
        }
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          logInfo(`WebSocket message received: ${message.type}`, 'Backend')

          // Handle different message types
          if (message.type === 'backend_message') {
            this.notifyBackendMessage({
              type: message.data.type,
              message: message.data.message,
              timestamp: message.data.timestamp,
              source: message.data.source || 'Backend'
            })
          } else if (message.type === 'pong') {
            logInfo('WebSocket pong received', 'Backend')
          } else if (message.type === 'connection_established') {
            logInfo(`WebSocket welcome message: ${message.message}`, 'Backend')
          }
        } catch (error) {
          logWarning('Failed to parse WebSocket message', 'Backend')
        }
      }

      ws.onclose = (event) => {
        logInfo(`WebSocket connection closed (code: ${event.code}, reason: ${event.reason})`, 'Backend')
      }

      ws.onerror = (error) => {
        logError(`WebSocket error occurred: ${error}`, 'Backend')
      }

      return ws
    } catch (error) {
      logError('Failed to connect WebSocket', 'Backend')
      return null
    }
  }

  // ==================== INSOLE V2 PIPELINE ENDPOINTS ====================

  /**
   * List available insole templates
   */
  async listTemplates(page: number = 1, pageSize: number = 50): Promise<TemplateListResponse> {
    return insoleV2Api.listTemplates(this.makeRequest.bind(this), page, pageSize)
  }

  /**
   * Perform initial alignment of STL with template
   */
  async alignInsole(request: AlignRequest): Promise<AlignResponse> {
    return insoleV2Api.alignInsole(this.makeRequest.bind(this), request)
  }

  /**
   * Adjust alignment manually (translate, rotate, scale)
   */
  async adjustAlignment(request: AlignAdjustRequest): Promise<AlignAdjustResponse> {
    return insoleV2Api.adjustAlignment(this.makeRequest.bind(this), request)
  }

  /**
   * Drape aligned template onto 3D foot scan
   */
  async drapeTemplate(request: DrapeRequest): Promise<DrapeResponse> {
    return insoleV2Api.drapeTemplate(this.makeRequest.bind(this), request)
  }

  /**
   * Adjust 3D boundary curve control points
   */
  async adjustBoundary(request: BoundaryAdjustRequest): Promise<BoundaryAdjustResponse> {
    return insoleV2Api.adjustBoundary(this.makeRequest.bind(this), request)
  }

  async smoothBoundary(request: BoundarySmoothRequest): Promise<BoundarySmoothResponse> {
    return insoleV2Api.smoothBoundary(this.makeRequest.bind(this), request)
  }

  async approveBoundary(request: BoundaryApproveRequest): Promise<BoundaryApproveResponse> {
    return insoleV2Api.approveBoundary(this.makeRequest.bind(this), request)
  }

  async generateInsoleV2(request: GenerateRequest): Promise<GenerateResponse> {
    return insoleV2Api.generateInsoleV2(this.makeRequest.bind(this), request)
  }

  async exportInsole(meshId: string, format: ExportFormat): Promise<ExportResponse> {
    return insoleV2Api.exportInsole(this.makeRequest.bind(this), meshId, format)
  }

  async approveInsole(meshId: string, request: ApproveRequest): Promise<ApproveResponse> {
    return insoleV2Api.approveInsole(this.makeRequest.bind(this), meshId, request)
  }

  // ---------------------------------------------------------------------- //
  // AI Assistant (Material Advisor / Print Debug)                          //
  // ---------------------------------------------------------------------- //

  async getAssistantHealth(): Promise<AssistantHealth> {
    return assistantApi.getAssistantHealth(this.makeRequest.bind(this))
  }

  async sendAssistantChat(
    topic: AssistantTopic,
    messages: AssistantChatTurn[],
    opts?: { model?: string; conversation_id?: string; page_context?: import('./api/assistantApi').PageContext },
  ): Promise<AssistantChatResponse> {
    return assistantApi.sendAssistantChat(this.makeRequest.bind(this), topic, messages, opts)
  }

  async listConversations(kind: AssistantTopic): Promise<ConversationSummary[]> {
    const isDoctor = !!this.doctorId
    return assistantApi.listConversations(this.makeRequest.bind(this), kind, isDoctor)
  }

  async createConversation(kind: AssistantTopic, title?: string): Promise<ConversationSummary> {
    const isDoctor = !!this.doctorId
    return assistantApi.createConversation(this.makeRequest.bind(this), kind, title, isDoctor)
  }

  async renameConversation(id: string, title: string): Promise<ConversationSummary> {
    const isDoctor = !!this.doctorId
    return assistantApi.renameConversation(this.makeRequest.bind(this), id, title, isDoctor)
  }

  async deleteConversation(id: string): Promise<void> {
    const isDoctor = !!this.doctorId
    return assistantApi.deleteConversation(this.makeRequest.bind(this), id, isDoctor)
  }

  async getConversationMessages(id: string): Promise<ConversationMessage[]> {
    const isDoctor = !!this.doctorId
    return assistantApi.getConversationMessages(this.makeRequest.bind(this), id, isDoctor)
  }

  async validateInsole(meshId: string): Promise<ValidateResponse> {
    return insoleV2Api.validateInsole(this.makeRequest.bind(this), meshId)
  }

  async traceTemplate(params: {
    file: File
    size_eu: number
    gender: string
    type: string
    name: string
    is_shared: boolean
    save: boolean
  }): Promise<import('./types').TemplateTraceResponse> {
    return insoleV2Api.traceTemplate(this.config.baseURL, this.authToken || '', params)
  }

  async deleteTemplate(templateId: string): Promise<{ deleted: boolean }> {
    return insoleV2Api.deleteTemplate(this.makeRequest.bind(this), templateId)
  }

  /** Hard-cancel an in-flight V2 pipeline session. */
  async cancelV2Session(sessionId: string) {
    return insoleV2Api.cancelV2Session(this.makeRequest.bind(this), sessionId)
  }

  /** Hard-cancel an in-flight Classic (V1) insole generation. */
  async cancelClassicInsole(projectId: string) {
    return insoleV2Api.cancelClassicInsole(this.makeRequest.bind(this), projectId)
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): boolean {
    return this.isConnected
  }

  /**
   * Get current doctor ID
   */
  getDoctorId(): string | null {
    return this.doctorId
  }

  // ── Farm AI token admin endpoints ─────────────────────────────────────────

  async getFarmAITokenBalance(farmId: string): Promise<{ balance: number; transactions: FarmAITokenTransaction[] }> {
    return this.makeRequest(`/api/admin/farms/${farmId}/ai-tokens`)
  }

  async creditFarmAITokens(farmId: string, amount: number, reason: string): Promise<void> {
    await this.makeRequest(`/api/admin/farms/${farmId}/ai-tokens/credit`, {
      method: 'POST',
      body: JSON.stringify({ amount, reason })
    })
  }

  async debitFarmAITokens(farmId: string, amount: number, reason: string): Promise<void> {
    await this.makeRequest(`/api/admin/farms/${farmId}/ai-tokens/debit`, {
      method: 'POST',
      body: JSON.stringify({ amount, reason })
    })
  }

  // ==================== PROFILE / ACCOUNT SETTINGS ====================

  /**
   * Update the authenticated user's profile fields.
   */
  async updateProfile(data: Record<string, any>): Promise<any> {
    return this.makeRequest('/api/user/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Change the authenticated user's password.
   */
  async changePassword(oldPassword: string, newPassword: string, newPasswordConfirm: string): Promise<any> {
    return this.makeRequest('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      }),
    })
  }

  /**
   * Get the authenticated user's full profile.
   */
  async getProfile(): Promise<any> {
    return this.makeRequest('/api/user/profile', {
      method: 'GET',
    })
  }
}

const PUBLIC_BACKEND_URL = 'https://gs-x1.gira-soles.com'
const LOCAL_BACKEND_URL = 'http://localhost:8000'

function buildLoopbackBackendUrl(hostname: string): string {
  const normalized = hostname.trim()

  if (normalized === '') {
    return LOCAL_BACKEND_URL
  }

  if (normalized.includes(':') && !normalized.startsWith('[')) {
    return `http://[${normalized}]:8000`
  }

  return `http://${normalized}:8000`
}

function isLoopbackHostname(hostname: string): boolean {
  const normalized = hostname.trim().toLowerCase()

  return normalized === 'localhost'
    || normalized === '0.0.0.0'
    || normalized === '::1'
    || normalized === '[::1]'
    || normalized === '127.0.0.1'
    || normalized.startsWith('127.')
    || normalized.endsWith('.localhost')
}

function isLoopbackUrl(url: string): boolean {
  try {
    return isLoopbackHostname(new URL(url).hostname)
  } catch {
    return false
  }
}

function getRuntimeHostname(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  return window.location.hostname
}

export function resolveBackendBaseUrl(envBaseUrl?: string, runtimeHostnameOverride?: string | null): string {
  const configuredBaseUrl = envBaseUrl?.trim()
  const runtimeHostname = runtimeHostnameOverride ?? getRuntimeHostname()
  const allowRemoteOnLocalhost = import.meta.env.VITE_ALLOW_REMOTE_BACKEND_ON_LOCALHOST === 'true'

  if (configuredBaseUrl) {
    const deployedFrontend = runtimeHostname && !isLoopbackHostname(runtimeHostname)
    const localFrontend = runtimeHostname && isLoopbackHostname(runtimeHostname)

    if (deployedFrontend && isLoopbackUrl(configuredBaseUrl)) {
      logWarning(
        `Ignoring loopback backend URL ${configuredBaseUrl} for deployed host ${runtimeHostname}; falling back to ${PUBLIC_BACKEND_URL}`,
        'Backend'
      )
      return PUBLIC_BACKEND_URL
    }

    if (localFrontend && !isLoopbackUrl(configuredBaseUrl) && !allowRemoteOnLocalhost) {
      const localFallback = buildLoopbackBackendUrl(runtimeHostname)
      logWarning(
        `Ignoring remote backend URL ${configuredBaseUrl} for localhost frontend ${runtimeHostname}; falling back to ${localFallback}. Set VITE_ALLOW_REMOTE_BACKEND_ON_LOCALHOST=true to keep remote backend.`,
        'Backend'
      )
      return localFallback
    }

    return configuredBaseUrl
  }

  if (runtimeHostname && isLoopbackHostname(runtimeHostname)) {
    return buildLoopbackBackendUrl(runtimeHostname)
  }

  return PUBLIC_BACKEND_URL
}

// Default configuration
const defaultConfig: BackendConfig = {
  baseURL: resolveBackendBaseUrl(import.meta.env.VITE_BACKEND_URL),
  healthCheckInterval: 30000, // 30 seconds
  timeout: 120000 // 120 seconds (needed for AI chat with local qwen3.5)
}

// Export singleton instance
export const backendService = new BackendService(defaultConfig)
